import type { ClientPackage } from '@coachapp/shared';
import { Minus, Plus, Ticket } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../lib/api-error';
import {
  useCreateClientPackageMutation,
  useGetClientPackagesQuery,
  useRestoreClientPackageSessionMutation,
  useUpdateClientPackageMutation,
  useUseClientPackageSessionMutation,
} from '../store/coachApi';
import ErrorAlert from './ErrorAlert';

interface ClientPackagesPanelProps {
  clientId: string;
}

const formatPrice = (priceCents: number) =>
  (priceCents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'badge-success';
    case 'DEPLETED':
      return 'badge-danger';
    case 'EXPIRED':
      return 'badge bg-amber-500/10 text-amber-700';
    default:
      return 'badge bg-slate-100 text-slate-600';
  }
};

function PackagePaidToggle({
  id,
  checked,
  disabled,
  readOnly,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: () => void;
  label: string;
}) {
  const tone = checked ? 'text-emerald-700' : 'text-amber-800';
  const box =
    'flex items-center gap-3 min-h-11 w-full flex-1 px-3 py-2 rounded-xl border text-sm font-semibold select-none ' +
    (checked ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100');

  if (readOnly) {
    return (
      <div className={box}>
        <span className={tone}>{label}</span>
      </div>
    );
  }

  return (
    <label htmlFor={id} className={`${box} cursor-pointer has-[:disabled]:opacity-50`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => onChange?.()}
        className="size-5 shrink-0 rounded-md border-slate-300 accent-primary"
      />
      <span className={tone}>{label}</span>
    </label>
  );
}

const ClientPackagesPanel = ({ clientId }: ClientPackagesPanelProps) => {
  const { t } = useTranslation();
  const { data: packages = [], isLoading, error: fetchError, refetch } = useGetClientPackagesQuery({
    clientId,
  });
  const [createClientPackage, { isLoading: saving }] = useCreateClientPackageMutation();
  const [updateClientPackage] = useUpdateClientPackageMutation();
  const [deductSession] = useUseClientPackageSessionMutation();
  const [restoreSession] = useRestoreClientPackageSessionMutation();
  const [showForm, setShowForm] = useState(false);
  const [using, setUsing] = useState<{ id: string; direction: 'use' | 'restore' } | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [totalSessions, setTotalSessions] = useState('');
  const [priceRubles, setPriceRubles] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadError = fetchError
    ? getApiErrorMessage(fetchError, 'Не удалось загрузить данные')
    : null;

  const resetForm = () => {
    setName('');
    setTotalSessions('');
    setPriceRubles('');
    setIsPaid(false);
    setShowForm(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const sessions = Number(totalSessions);
    const price = Number(priceRubles);
    if (!name.trim() || !sessions || sessions <= 0 || Number.isNaN(price) || price < 0) {
      return;
    }

    setError(null);
    try {
      await createClientPackage({
        clientId,
        name: name.trim(),
        totalSessions: sessions,
        priceCents: Math.round(price * 100),
        paidAt: isPaid ? new Date().toISOString() : undefined,
      }).unwrap();
      resetForm();
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(err, 'Не удалось создать пакет'),
      );
    }
  };

  const handleTogglePaid = async (pkg: ClientPackage) => {
    if (pkg.status !== 'ACTIVE') {
      return;
    }

    setUpdatingId(pkg.id);
    setError(null);
    try {
      await updateClientPackage({
        clientId,
        id: pkg.id,
        data: { paidAt: pkg.paidAt ? null : new Date().toISOString() },
      }).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось обновить пакет'));
    } finally {
      setUpdatingId(null);
    }
  };

  const canAdjustPackage = (pkg: ClientPackage) =>
    pkg.status === 'ACTIVE' || pkg.status === 'DEPLETED';

  const handleAdjustSession = async (pkg: ClientPackage, direction: 'use' | 'restore') => {
    if (!canAdjustPackage(pkg)) {
      return;
    }
    if (direction === 'use' && pkg.usedSessions >= pkg.totalSessions) {
      return;
    }
    if (direction === 'restore' && pkg.usedSessions <= 0) {
      return;
    }

    setUsing({ id: pkg.id, direction });
    setError(null);
    try {
      if (direction === 'use') {
        await deductSession({ clientId, id: pkg.id }).unwrap();
      } else {
        await restoreSession({ clientId, id: pkg.id }).unwrap();
      }
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(
              err,
              direction === 'use' ? 'Не удалось списать занятие' : 'Не удалось вернуть занятие',
            ),
      );
    } finally {
      setUsing(null);
    }
  };

  return (
    <div className="card p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-slate-900">{t('Пакеты занятий')}</h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="btn-primary text-sm py-2 w-full sm:w-auto shrink-0"
          >
            <Plus size={16} />
            <span>{t('Пакет')}</span>
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-5 p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
          <div>
            <label className="section-label mb-1 block">
              {t('Название пакета')}
            </label>
            <input
              type="text"
              className="input-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('Название пакета')}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="section-label mb-1 block">
                {t('Количество занятий')}
              </label>
              <input
                type="number"
                min={1}
                className="input-field"
                value={totalSessions}
                onChange={(e) => setTotalSessions(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="section-label mb-1 block">
                {t('Цена, ₽')}
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="input-field"
                value={priceRubles}
                onChange={(e) => setPriceRubles(e.target.value)}
                required
              />
            </div>
          </div>
          <PackagePaidToggle
            id="package-paid"
            checked={isPaid}
            onChange={() => setIsPaid((v) => !v)}
            label={t('Оплачен')}
          />
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2">
              {saving ? t('Сохранение...') : t('Сохранить')}
            </button>
            <button type="button" onClick={resetForm} className="btn-secondary text-sm py-2">
              {t('Отмена')}
            </button>
          </div>
        </form>
      )}

      {(error || loadError) && (
        <ErrorAlert
          message={error || loadError!}
          onRetry={() => void refetch()}
          onDismiss={() => setError(null)}
          className="mb-4"
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="spinner h-8 w-8" />
        </div>
      ) : packages.length === 0 ? (
        <div className="empty-state py-8">
          <Ticket size={40} className="mb-3 opacity-20" />
          <p className="text-sm text-slate-500">{t('Нет пакетов')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="@container p-4 rounded-2xl border border-slate-100 bg-white hover:border-slate-200 transition-colors"
            >
              <div className="min-w-0 space-y-3">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-slate-900 truncate">{pkg.name}</h3>
                    <span className={statusBadgeClass(pkg.status)}>
                      {t(`packageStatus.${pkg.status}`, pkg.status)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                    <span>
                      {t('Занятия')}:{' '}
                      <span className="font-semibold text-slate-900">
                        {pkg.usedSessions}/{pkg.totalSessions}
                      </span>
                    </span>
                    <span>
                      {t('Цена')}:{' '}
                      <span className="font-semibold text-slate-900">
                        {formatPrice(pkg.priceCents)} ₽
                      </span>
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 @min-[28rem]:flex-row @min-[28rem]:items-stretch sm:flex-row sm:items-stretch">
                  <PackagePaidToggle
                    id={`pkg-paid-${pkg.id}`}
                    checked={Boolean(pkg.paidAt)}
                    disabled={updatingId === pkg.id}
                    readOnly={pkg.status !== 'ACTIVE'}
                    onChange={() => void handleTogglePaid(pkg)}
                    label={pkg.paidAt ? t('Оплачен') : t('Не оплачен')}
                  />
                  <div className="flex gap-2 w-full @min-[28rem]:w-auto sm:w-auto shrink-0">
                    <button
                      type="button"
                      onClick={() => void handleAdjustSession(pkg, 'restore')}
                      disabled={
                        using?.id === pkg.id ||
                        !canAdjustPackage(pkg) ||
                        pkg.usedSessions <= 0
                      }
                      aria-label={t('Вернуть занятие')}
                      className="btn-secondary text-sm py-2 min-h-11 min-w-11 px-3 disabled:opacity-50"
                    >
                      {using?.id === pkg.id && using.direction === 'restore' ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      ) : (
                        <Plus size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleAdjustSession(pkg, 'use')}
                      disabled={
                        using?.id === pkg.id ||
                        !canAdjustPackage(pkg) ||
                        pkg.usedSessions >= pkg.totalSessions
                      }
                      aria-label={t('Списать занятие')}
                      className="btn-secondary text-sm py-2 min-h-11 w-full @min-[28rem]:w-auto sm:w-auto disabled:opacity-50"
                    >
                      {using?.id === pkg.id && using.direction === 'use' ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      ) : (
                        <Minus size={16} />
                      )}
                      <span>{t('1 занятие')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientPackagesPanel;
